import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSSEConnected } from '../../hooks/useSSE'
import { useAuth } from '../../context/AuthContext'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import { timeAgo } from '../../hooks/useTimeAgo'

type Folder = 'inbox' | 'sent' | 'archive' | 'trash'

interface PortalMessage {
  id: string
  from_type: string
  from_id: string
  to_type: string
  to_id: string
  cc?: { type: string; id: string }[]
  subject: string
  body: string
  folder: string
  is_read: number
  created_at: string
  attachments?: string[]
}

const FOLDERS: { value: Folder; label: string; icon: string }[] = [
  { value: 'inbox', label: 'Inbox', icon: '📥' },
  { value: 'sent', label: 'Sent', icon: '📤' },
  { value: 'archive', label: 'Archive', icon: '📦' },
  { value: 'trash', label: 'Trash', icon: '🗑️' },
]

function displaySender(msg: PortalMessage): string {
  if (msg.from_type === 'persona') {
    return msg.from_id[0].toUpperCase() + msg.from_id.slice(1)
  }
  return msg.from_id || 'Unknown'
}

export default function CommunityMessagesPage() {
  const { user } = useAuth()
  const sseOk = useSSEConnected()
  const queryClient = useQueryClient()
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

  const [folder, setFolder] = useState<Folder>('inbox')
  const [activeMsg, setActiveMsg] = useState<PortalMessage | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showCompose, setShowCompose] = useState(false)
  const [composeData, setComposeData] = useState({ to: '', cc: '', subject: '', body: '' })
  const [replyTo, setReplyTo] = useState<PortalMessage | null>(null)

  // Fetch messages
  const { data: messages, isLoading } = useQuery({
    queryKey: ['portalMessages', folder],
    queryFn: async () => {
      const res = await fetch(`/api/messages?folder=${folder}`, { headers })
      if (!res.ok) return []
      const data = await res.json()
      return (data.messages || data || []) as PortalMessage[]
    },
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  // Fetch personas for compose dropdown
  const { data: personas } = useQuery({
    queryKey: ['communityPersonas', user?.communityId],
    queryFn: async () => {
      const cid = user?.communityId || `portal-${user?.communitySlug || ''}`
      const res = await fetch(`/api/communities/${cid}/personas`, { headers })
      if (!res.ok) return []
      const data = await res.json()
      return (data.personas || []) as string[]
    },
  })

  // Mark read
  const markReadMut = useMutation({
    mutationFn: async (msgId: string) => {
      await fetch(`/api/messages/${msgId}/read`, { method: 'PATCH', headers })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portalMessages'] }),
  })

  // Move to folder
  const moveMut = useMutation({
    mutationFn: async ({ ids, target }: { ids: string[]; target: string }) => {
      await Promise.all(ids.map(id =>
        fetch(`/api/messages/${id}`, {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ folder: target }),
        })
      ))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalMessages'] })
      setSelected(new Set())
    },
  })

  // Send message
  const sendMut = useMutation({
    mutationFn: async (data: { to: string; cc?: string; subject: string; body: string }) => {
      const res = await fetch('/api/messages', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_type: 'persona',
          to_id: data.to,
          cc: data.cc ? data.cc.split(',').map(s => ({ type: 'user', id: s.trim() })) : [],
          subject: data.subject,
          body: data.body,
        }),
      })
      if (!res.ok) throw new Error('Send failed')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalMessages'] })
      setShowCompose(false)
      setReplyTo(null)
      setComposeData({ to: '', cc: '', subject: '', body: '' })
    },
  })

  const unreadCount = useMemo(() => (messages || []).filter(m => !m.is_read).length, [messages])

  function openMessage(msg: PortalMessage) {
    setActiveMsg(msg)
    if (!msg.is_read) markReadMut.mutate(msg.id)
  }

  function toggleSelect(id: string) {
    const next = new Set(selected)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelected(next)
  }

  function startReply(msg: PortalMessage) {
    setReplyTo(msg)
    setComposeData({
      to: msg.from_id,
      cc: '',
      subject: msg.subject.startsWith('Re: ') ? msg.subject : `Re: ${msg.subject}`,
      body: '',
    })
    setShowCompose(true)
  }

  // ═══ MESSAGE DETAIL VIEW ═══
  if (activeMsg) {
    return (
      <div className="flex flex-col h-full">
        <div className="shrink-0 flex items-center gap-3 px-6 py-3" style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
          <button
            onClick={() => setActiveMsg(null)}
            style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 14 }}
          >
            ← Back
          </button>
          <span className="text-sm font-semibold flex-1 truncate" style={{ color: 'var(--text-primary)' }}>
            {activeMsg.subject || '(No Subject)'}
          </span>
          <button
            onClick={() => startReply(activeMsg)}
            className="px-3 py-1 rounded text-xs font-medium"
            style={{ background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}
          >
            Reply
          </button>
          <button
            onClick={() => { moveMut.mutate({ ids: [activeMsg.id], target: 'archive' }); setActiveMsg(null) }}
            className="px-3 py-1 rounded text-xs"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}
          >
            Archive
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <PersonaAvatar name={activeMsg.from_id} size={40} />
              <div>
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {displaySender(activeMsg)}
                </div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {timeAgo(activeMsg.created_at)}
                </div>
              </div>
            </div>
            <div className="rounded-lg p-4 text-sm leading-relaxed" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
              <MarkdownRenderer content={activeMsg.body} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ═══ COMPOSE MODAL ═══
  const composeModal = showCompose ? (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-xl w-full max-w-lg p-6" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {replyTo ? `Reply to ${displaySender(replyTo)}` : 'New Message'}
          </h3>
          <button onClick={() => { setShowCompose(false); setReplyTo(null) }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>To</label>
            <select
              value={composeData.to}
              onChange={e => setComposeData(d => ({ ...d, to: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            >
              <option value="">Select persona...</option>
              {(personas || []).map(p => {
                const name = typeof p === 'string' ? p : (p as any).name
                return <option key={name} value={name}>{name[0].toUpperCase() + name.slice(1)}</option>
              })}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Subject</label>
            <input
              value={composeData.subject}
              onChange={e => setComposeData(d => ({ ...d, subject: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              placeholder="Subject"
            />
          </div>

          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Message</label>
            <textarea
              value={composeData.body}
              onChange={e => setComposeData(d => ({ ...d, body: e.target.value }))}
              rows={6}
              className="w-full px-3 py-2 rounded-lg text-sm resize-none"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              placeholder="Write your message..."
            />
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowCompose(false); setReplyTo(null) }}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={() => sendMut.mutate(composeData)}
              disabled={!composeData.to || !composeData.body.trim() || sendMut.isPending}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{
                background: composeData.to && composeData.body.trim() ? 'var(--accent)' : 'var(--bg-elevated)',
                color: composeData.to && composeData.body.trim() ? '#fff' : 'var(--text-muted)',
                border: 'none',
                cursor: composeData.to && composeData.body.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              {sendMut.isPending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null

  // ═══ LIST VIEW ═══
  return (
    <div className="flex h-full">
      {composeModal}

      {/* Folders sidebar */}
      <div className="w-48 shrink-0 py-4 px-2" style={{ background: 'var(--bg-surface)', borderRight: '1px solid var(--border)' }}>
        <button
          onClick={() => { setShowCompose(true); setReplyTo(null); setComposeData({ to: '', cc: '', subject: '', body: '' }) }}
          className="w-full px-3 py-2 rounded-lg text-xs font-semibold mb-4"
          style={{ background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          + Compose
        </button>
        {FOLDERS.map(f => (
          <button
            key={f.value}
            onClick={() => { setFolder(f.value); setSelected(new Set()) }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-left mb-0.5"
            style={{
              background: folder === f.value ? 'var(--bg-elevated)' : 'transparent',
              color: folder === f.value ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <span>{f.icon}</span>
            <span className="flex-1">{f.label}</span>
            {f.value === 'inbox' && unreadCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ background: 'var(--accent)', color: '#fff', fontSize: 9 }}>
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Message list */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 px-4 py-2" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{selected.size} selected</span>
            <button
              onClick={() => moveMut.mutate({ ids: [...selected], target: 'archive' })}
              className="text-xs px-2 py-1 rounded"
              style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}
            >
              Archive
            </button>
            <button
              onClick={() => moveMut.mutate({ ids: [...selected], target: 'trash' })}
              className="text-xs px-2 py-1 rounded"
              style={{ background: 'var(--bg-surface)', color: 'var(--danger)', border: '1px solid var(--border)', cursor: 'pointer' }}
            >
              Delete
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs px-2 py-1 rounded"
              style={{ background: 'none', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}
            >
              Clear
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="text-sm py-8 text-center" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
          ) : (messages || []).length === 0 ? (
            <div className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>
              {folder === 'inbox' ? 'Your inbox is empty.' : `No messages in ${folder}.`}
            </div>
          ) : (
            (messages || []).map(msg => (
              <div
                key={msg.id}
                onClick={() => openMessage(msg)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
                style={{
                  borderBottom: '1px solid var(--border)',
                  background: selected.has(msg.id) ? 'var(--accent-muted)' : 'transparent',
                  fontWeight: msg.is_read ? 400 : 600,
                }}
                onMouseEnter={e => { if (!selected.has(msg.id)) (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)' }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = selected.has(msg.id) ? 'var(--accent-muted)' : 'transparent' }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(msg.id)}
                  onChange={e => { e.stopPropagation(); toggleSelect(msg.id) }}
                  onClick={e => e.stopPropagation()}
                  className="shrink-0"
                />
                <PersonaAvatar name={msg.from_id} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs truncate" style={{ color: msg.is_read ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                      {displaySender(msg)}
                    </span>
                    {!msg.is_read && (
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--accent)' }} />
                    )}
                  </div>
                  <div className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>
                    {msg.subject || '(No Subject)'}
                  </div>
                  <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                    {msg.body?.substring(0, 80)}
                  </div>
                </div>
                <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                  {timeAgo(msg.created_at)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
